/**
 * Wizard Steg 4: Kapital & Disposition
 *
 * Del 1: Förändringar i eget kapital (K2 5.7, ÅRL 5 kap 14§)
 * Del 2: Resultatdisposition (K2 5.8)
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  ArrowRight,
  ArrowLeft,
  Wallet,
  AlertCircle,
  Info,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Building2,
} from "lucide-react";
import { reportsApi, type AnnualReport, type UserInput, type ParsedSieData } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { toast } from "sonner";
import { isManualEntry } from "@/lib/manual-entry";

interface Step4DispositionProps {
  report: AnnualReport;
  onUpdate: (report: AnnualReport) => void;
  /** Override save (for guest/public wizard) */
  onSaveInput?: (data: Record<string, unknown>) => Promise<{ success: boolean; report?: AnnualReport; error?: string }>;
  /** Override navigation (for guest/public wizard or K3 decimal steps) */
  onNavigateStep?: (step: number | string) => void;
  /** Previous step (default: 3, guest mode: 4) */
  prevStep?: number | string;
  /** Next step (default: 5, guest mode: 6) */
  nextStep?: number | string;
}

interface EquityChangesFormData {
  // Bekräfta att användaren granskat EK-förändringar
  hasReviewedEquityChanges: boolean;

  // Utbetald utdelning (föregående års beslut)
  dividendPaidDuringYear: number;

  // Aktieägartillskott detaljer
  shareholderContributionType?: 'unconditional' | 'conditional';
  shareholderContributionContributor?: string;

  // Nyemission detaljer (om detekterad)
  shareIssueType?: 'cash' | 'non_cash';
  shareIssueDecisionDate?: string;
}

interface DispositionFormData {
  proposedDividend: number;
}

type FormData = EquityChangesFormData & DispositionFormData;

// Hjälpfunktion för att formatera förändringstyp till svenska
function formatChangeType(type: string): string {
  const labels: Record<string, string> = {
    'share_issue': 'Nyemission',
    'bonus_issue': 'Fondemission',
    'capital_reduction': 'Kapitalnedsättning',
    'shareholder_contribution': 'Aktieägartillskott',
    'contribution_repayment': 'Återbetalning av tillskott',
    'dividend_paid': 'Utbetald utdelning',
    'group_contribution_received': 'Mottaget koncernbidrag',
    'group_contribution_given': 'Lämnat koncernbidrag',
    'reserve_transfer_to': 'Avsättning till reservfond',
    'reserve_transfer_from': 'Ianspråktagande av reservfond',
    'loss_coverage': 'Täckning av förlust',
    'previous_year_result': 'Disp. föregående års resultat',
    'other': 'Övrig förändring',
  };
  return labels[type] || type;
}

export function Step4Disposition({ report, onUpdate, onSaveInput, onNavigateStep, prevStep = 3, nextStep = 5 }: Step4DispositionProps) {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEquitySection, setShowEquitySection] = useState(true);

  const sieData = report.parsedSieData as ParsedSieData | null;
  const userInput = report.userInput as UserInput;

  // Check if this is manual entry (no SIE file)
  const isManual = isManualEntry(sieData);

  // Equity change analysis from SIE parser
  const equityAnalysis = sieData?.equityChangeAnalysis;

  // Manual equity changes from Step2 (userInput.equityChanges)
  const manualEquityChanges = userInput.equityChanges;

  // Key figures for calculations
  const equity = sieData?.balanceSheet.equityAndLiabilities.equity;
  const retainedEarnings = equity?.retainedEarnings || 0;
  const currentYearResult = equity?.currentYearResult || 0;
  const freeEquity = sieData?.keyFigures.freeEquity || 0;

  // Available for distribution
  const availableForDistribution = retainedEarnings + currentYearResult;

  // Check if there are manual equity changes from Step2
  const hasManualEquityChanges = useMemo(() => {
    if (!manualEquityChanges) return false;

    // Check all possible manual entry fields
    const hasShareIssue = manualEquityChanges.shareIssue?.totalShareCapitalIncrease &&
      manualEquityChanges.shareIssue.totalShareCapitalIncrease > 0;
    const hasShareholderContributions = manualEquityChanges.shareholderContributions &&
      manualEquityChanges.shareholderContributions.length > 0 &&
      manualEquityChanges.shareholderContributions.some(c => c.amount && c.amount > 0);
    const hasDividendPaid = manualEquityChanges.dividendPaidDuringYear &&
      manualEquityChanges.dividendPaidDuringYear > 0;
    const hasGroupContributionReceived = manualEquityChanges.groupContributionReceived &&
      manualEquityChanges.groupContributionReceived > 0;
    const hasGroupContributionGiven = manualEquityChanges.groupContributionGiven &&
      manualEquityChanges.groupContributionGiven > 0;
    const hasOtherChanges = manualEquityChanges.otherChanges &&
      manualEquityChanges.otherChanges.length > 0 &&
      manualEquityChanges.otherChanges.some(c => c.amount !== 0);
    const hasReserveFundTransfers = manualEquityChanges.reserveFundTransfers &&
      manualEquityChanges.reserveFundTransfers.length > 0 &&
      manualEquityChanges.reserveFundTransfers.some(t => t.amount > 0);
    const hasBonusIssue = manualEquityChanges.bonusIssue?.amount &&
      manualEquityChanges.bonusIssue.amount > 0;
    const hasCapitalReduction = manualEquityChanges.capitalReduction?.amount &&
      manualEquityChanges.capitalReduction.amount > 0;
    const hasContributionRepayments = manualEquityChanges.contributionRepayments &&
      manualEquityChanges.contributionRepayments.length > 0 &&
      manualEquityChanges.contributionRepayments.some(r => r.amount > 0);

    return hasShareIssue || hasShareholderContributions || hasDividendPaid ||
           hasGroupContributionReceived || hasGroupContributionGiven ||
           hasOtherChanges || hasReserveFundTransfers || hasBonusIssue ||
           hasCapitalReduction || hasContributionRepayments;
  }, [manualEquityChanges]);

  // Check if there are any meaningful equity changes to show (SIE-detected OR manual)
  const hasEquityChanges = useMemo(() => {
    const hasSieChanges = equityAnalysis?.summary.hasAnyChanges || false;
    return hasSieChanges || hasManualEquityChanges;
  }, [equityAnalysis, hasManualEquityChanges]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSOLIDATED EQUITY DATA - Combines SIE-detected + Manual changes
  // According to K2 5.7, ÅRL 5 kap 14§
  // ═══════════════════════════════════════════════════════════════════════════
  interface ConsolidatedChange {
    type: string;
    amount: number;
    description?: string;
    source: 'sie' | 'manual';
    requiresUserInput?: boolean;
  }

  interface ConsolidatedEquityComponent {
    label: string;
    opening: number;
    closing: number;
    changes: ConsolidatedChange[];
    requiresInput: boolean;
  }

  const consolidatedEquityData = useMemo(() => {
    // Helper to map SIE changes to consolidated format
    const mapSieChanges = (changes: Array<{ type: string; amount: number; requiresUserInput?: boolean }> | undefined): ConsolidatedChange[] => {
      if (!changes) return [];
      return changes.map(c => ({
        type: c.type,
        amount: c.amount,
        source: 'sie' as const,
        requiresUserInput: c.requiresUserInput,
      }));
    };

    // Start with SIE values as base (or zeros if no SIE data)
    const baseShareCapital: { opening: number; closing: number; changes: ConsolidatedChange[] } = {
      opening: equityAnalysis?.shareCapital?.opening ?? 0,
      closing: equityAnalysis?.shareCapital?.closing ?? 0,
      changes: mapSieChanges(equityAnalysis?.shareCapital?.changes),
    };

    const baseLegalReserve: { opening: number; closing: number; changes: ConsolidatedChange[] } = {
      opening: equityAnalysis?.legalReserve?.opening ?? 0,
      closing: equityAnalysis?.legalReserve?.closing ?? 0,
      changes: mapSieChanges(equityAnalysis?.legalReserve?.changes),
    };

    const baseSharePremium: { opening: number; closing: number; changes: ConsolidatedChange[] } = {
      opening: equityAnalysis?.sharePremium?.opening ?? 0,
      closing: equityAnalysis?.sharePremium?.closing ?? 0,
      changes: mapSieChanges(equityAnalysis?.sharePremium?.changes),
    };

    const baseRetainedEarnings: { opening: number; closing: number; changes: ConsolidatedChange[] } = {
      opening: equityAnalysis?.retainedEarnings?.opening ?? 0,
      closing: equityAnalysis?.retainedEarnings?.closing ?? 0,
      changes: mapSieChanges(equityAnalysis?.retainedEarnings?.changes),
    };

    const baseShareholderContributions: { opening: number; closing: number; changes: ConsolidatedChange[] } = {
      opening: equityAnalysis?.shareholderContributions?.opening ?? 0,
      closing: equityAnalysis?.shareholderContributions?.closing ?? 0,
      changes: mapSieChanges(equityAnalysis?.shareholderContributions?.changes),
    };

    const baseRevaluationReserve: { opening: number; closing: number; changes: ConsolidatedChange[] } = {
      opening: equityAnalysis?.revaluationReserve?.opening ?? 0,
      closing: equityAnalysis?.revaluationReserve?.closing ?? 0,
      changes: mapSieChanges(equityAnalysis?.revaluationReserve?.changes),
    };

    const baseDevelopmentFund: { opening: number; closing: number; changes: ConsolidatedChange[] } = {
      opening: equityAnalysis?.developmentFund?.opening ?? 0,
      closing: equityAnalysis?.developmentFund?.closing ?? 0,
      changes: mapSieChanges(equityAnalysis?.developmentFund?.changes),
    };

    // Now add manual changes from Step2 (if not already detected in SIE)
    if (manualEquityChanges) {
      // ─────────────────────────────────────────────────────────────────────
      // Nyemission (affects Aktiekapital + potentially Överkursfond)
      // ─────────────────────────────────────────────────────────────────────
      if (manualEquityChanges.shareIssue?.totalShareCapitalIncrease && manualEquityChanges.shareIssue.totalShareCapitalIncrease > 0) {
        // Check if SIE already detected share capital increase
        const sieDetectedShareCapitalIncrease = baseShareCapital.changes.some(
          c => c.type === 'share_issue' && c.amount > 0
        );
        if (!sieDetectedShareCapitalIncrease) {
          baseShareCapital.changes.push({
            type: 'share_issue',
            amount: manualEquityChanges.shareIssue.totalShareCapitalIncrease,
            description: manualEquityChanges.shareIssue.type === 'non_cash' ? 'Apportemission' : 'Nyemission (kontant)',
            source: 'manual',
          });
        }
        // Share premium from nyemission
        if (manualEquityChanges.shareIssue.sharePremiumAmount && manualEquityChanges.shareIssue.sharePremiumAmount > 0) {
          const sieDetectedSharePremium = baseSharePremium.changes.some(
            c => c.type === 'share_issue' && c.amount > 0
          );
          if (!sieDetectedSharePremium) {
            baseSharePremium.changes.push({
              type: 'share_issue',
              amount: manualEquityChanges.shareIssue.sharePremiumAmount,
              description: 'Överkurs vid nyemission',
              source: 'manual',
            });
          }
        }
      }

      // ─────────────────────────────────────────────────────────────────────
      // Fondemission (moves between equity components, no net EK change)
      // ─────────────────────────────────────────────────────────────────────
      if (manualEquityChanges.bonusIssue?.amount && manualEquityChanges.bonusIssue.amount > 0) {
        const sourceLabel = manualEquityChanges.bonusIssue.sourceAccount === 'sharePremium'
          ? 'överkursfond'
          : manualEquityChanges.bonusIssue.sourceAccount === 'legalReserve'
            ? 'reservfond'
            : 'balanserat resultat';

        baseShareCapital.changes.push({
          type: 'bonus_issue',
          amount: manualEquityChanges.bonusIssue.amount,
          description: `Fondemission (från ${sourceLabel})`,
          source: 'manual',
        });

        // Deduct from source
        if (manualEquityChanges.bonusIssue.sourceAccount === 'sharePremium') {
          baseSharePremium.changes.push({
            type: 'bonus_issue',
            amount: -manualEquityChanges.bonusIssue.amount,
            description: 'Till fondemission',
            source: 'manual',
          });
        } else if (manualEquityChanges.bonusIssue.sourceAccount === 'legalReserve') {
          baseLegalReserve.changes.push({
            type: 'bonus_issue',
            amount: -manualEquityChanges.bonusIssue.amount,
            description: 'Till fondemission',
            source: 'manual',
          });
        } else {
          baseRetainedEarnings.changes.push({
            type: 'bonus_issue',
            amount: -manualEquityChanges.bonusIssue.amount,
            description: 'Till fondemission',
            source: 'manual',
          });
        }
      }

      // ─────────────────────────────────────────────────────────────────────
      // Kapitalnedsättning
      // ─────────────────────────────────────────────────────────────────────
      if (manualEquityChanges.capitalReduction?.amount && manualEquityChanges.capitalReduction.amount > 0) {
        const purposeLabel = manualEquityChanges.capitalReduction.purpose === 'loss_coverage'
          ? 'för täckning av förlust'
          : manualEquityChanges.capitalReduction.purpose === 'repayment_to_shareholders'
            ? 'för återbetalning till aktieägare'
            : 'för avsättning till reservfond';

        baseShareCapital.changes.push({
          type: 'capital_reduction',
          amount: -manualEquityChanges.capitalReduction.amount,
          description: `Kapitalnedsättning (${purposeLabel})`,
          source: 'manual',
        });

        // Credit to destination
        if (manualEquityChanges.capitalReduction.purpose === 'loss_coverage') {
          baseRetainedEarnings.changes.push({
            type: 'capital_reduction',
            amount: manualEquityChanges.capitalReduction.amount,
            description: 'Från kapitalnedsättning',
            source: 'manual',
          });
        } else if (manualEquityChanges.capitalReduction.purpose === 'transfer_to_reserve') {
          baseLegalReserve.changes.push({
            type: 'capital_reduction',
            amount: manualEquityChanges.capitalReduction.amount,
            description: 'Från kapitalnedsättning',
            source: 'manual',
          });
        }
        // repayment_to_shareholders reduces total EK (cash out)
      }

      // ─────────────────────────────────────────────────────────────────────
      // Aktieägartillskott
      // ─────────────────────────────────────────────────────────────────────
      if (manualEquityChanges.shareholderContributions && manualEquityChanges.shareholderContributions.length > 0) {
        manualEquityChanges.shareholderContributions
          .filter(c => c.amount && c.amount > 0)
          .forEach(contribution => {
            // Check if SIE already detected this
            const sieDetected = baseShareholderContributions.changes.some(
              c => c.type === 'shareholder_contribution' && Math.abs(c.amount - (contribution.amount || 0)) < 1
            );
            if (!sieDetected) {
              baseShareholderContributions.changes.push({
                type: 'shareholder_contribution',
                amount: contribution.amount || 0,
                description: `${contribution.type === 'conditional' ? 'Villkorat' : 'Ovillkorat'} tillskott${contribution.contributorName ? ` från ${contribution.contributorName}` : ''}`,
                source: 'manual',
              });
            }
          });
      }

      // ─────────────────────────────────────────────────────────────────────
      // Återbetalning av tillskott
      // ─────────────────────────────────────────────────────────────────────
      if (manualEquityChanges.contributionRepayments && manualEquityChanges.contributionRepayments.length > 0) {
        manualEquityChanges.contributionRepayments
          .filter(r => r.amount > 0)
          .forEach(repayment => {
            baseShareholderContributions.changes.push({
              type: 'contribution_repayment',
              amount: -repayment.amount,
              description: `Återbetalning${repayment.recipientName ? ` till ${repayment.recipientName}` : ''}`,
              source: 'manual',
            });
          });
      }

      // ─────────────────────────────────────────────────────────────────────
      // Utbetald utdelning (beslut föregående årsstämma)
      // ─────────────────────────────────────────────────────────────────────
      if (manualEquityChanges.dividendPaidDuringYear && manualEquityChanges.dividendPaidDuringYear > 0) {
        const sieDetectedDividend = baseRetainedEarnings.changes.some(
          c => c.type === 'dividend_paid' && c.amount < 0
        );
        if (!sieDetectedDividend) {
          baseRetainedEarnings.changes.push({
            type: 'dividend_paid',
            amount: -manualEquityChanges.dividendPaidDuringYear,
            description: 'Utbetald utdelning (beslut föregående årsstämma)',
            source: 'manual',
          });
        }
      }

      // ─────────────────────────────────────────────────────────────────────
      // Koncernbidrag
      // ─────────────────────────────────────────────────────────────────────
      if (manualEquityChanges.groupContributionReceived && manualEquityChanges.groupContributionReceived > 0) {
        baseRetainedEarnings.changes.push({
          type: 'group_contribution_received',
          amount: manualEquityChanges.groupContributionReceived,
          description: 'Mottaget koncernbidrag',
          source: 'manual',
        });
      }
      if (manualEquityChanges.groupContributionGiven && manualEquityChanges.groupContributionGiven > 0) {
        baseRetainedEarnings.changes.push({
          type: 'group_contribution_given',
          amount: -manualEquityChanges.groupContributionGiven,
          description: 'Lämnat koncernbidrag',
          source: 'manual',
        });
      }

      // ─────────────────────────────────────────────────────────────────────
      // Reservfondstransaktioner
      // ─────────────────────────────────────────────────────────────────────
      if (manualEquityChanges.reserveFundTransfers && manualEquityChanges.reserveFundTransfers.length > 0) {
        manualEquityChanges.reserveFundTransfers
          .filter(t => t.amount > 0)
          .forEach(transfer => {
            if (transfer.direction === 'to') {
              baseLegalReserve.changes.push({
                type: 'reserve_transfer_to',
                amount: transfer.amount,
                description: `Avsättning${transfer.reason ? ` (${transfer.reason})` : ''}`,
                source: 'manual',
              });
              baseRetainedEarnings.changes.push({
                type: 'reserve_transfer_to',
                amount: -transfer.amount,
                description: 'Till reservfond',
                source: 'manual',
              });
            } else {
              baseLegalReserve.changes.push({
                type: 'reserve_transfer_from',
                amount: -transfer.amount,
                description: `Ianspråktagande${transfer.reason ? ` (${transfer.reason})` : ''}`,
                source: 'manual',
              });
              baseRetainedEarnings.changes.push({
                type: 'reserve_transfer_from',
                amount: transfer.amount,
                description: 'Från reservfond',
                source: 'manual',
              });
            }
          });
      }

      // ─────────────────────────────────────────────────────────────────────
      // Övriga förändringar
      // ─────────────────────────────────────────────────────────────────────
      if (manualEquityChanges.otherChanges && manualEquityChanges.otherChanges.length > 0) {
        manualEquityChanges.otherChanges
          .filter(c => c.amount !== 0)
          .forEach(change => {
            const target =
              change.component === 'shareCapital' ? baseShareCapital :
              change.component === 'legalReserve' ? baseLegalReserve :
              change.component === 'sharePremium' ? baseSharePremium :
              baseRetainedEarnings;
            target.changes.push({
              type: 'other',
              amount: change.amount,
              description: change.description || 'Övrig förändring',
              source: 'manual',
            });
          });
      }
    }

    // Calculate net changes for each component
    const calculateNetChange = (changes: ConsolidatedChange[]) =>
      changes.reduce((sum, c) => sum + c.amount, 0);

    // Build the final consolidated structure
    const components: ConsolidatedEquityComponent[] = [
      // Bundet eget kapital
      {
        label: 'Aktiekapital',
        opening: baseShareCapital.opening,
        closing: baseShareCapital.closing !== 0 ? baseShareCapital.closing : baseShareCapital.opening + calculateNetChange(baseShareCapital.changes),
        changes: baseShareCapital.changes,
        requiresInput: baseShareCapital.changes.some(c => c.requiresUserInput),
      },
      {
        label: 'Reservfond',
        opening: baseLegalReserve.opening,
        closing: baseLegalReserve.closing !== 0 ? baseLegalReserve.closing : baseLegalReserve.opening + calculateNetChange(baseLegalReserve.changes),
        changes: baseLegalReserve.changes,
        requiresInput: baseLegalReserve.changes.some(c => c.requiresUserInput),
      },
      {
        label: 'Uppskrivningsfond',
        opening: baseRevaluationReserve.opening,
        closing: baseRevaluationReserve.closing !== 0 ? baseRevaluationReserve.closing : baseRevaluationReserve.opening + calculateNetChange(baseRevaluationReserve.changes),
        changes: baseRevaluationReserve.changes,
        requiresInput: baseRevaluationReserve.changes.some(c => c.requiresUserInput),
      },
      {
        label: 'Fond för utvecklingsutg.',
        opening: baseDevelopmentFund.opening,
        closing: baseDevelopmentFund.closing !== 0 ? baseDevelopmentFund.closing : baseDevelopmentFund.opening + calculateNetChange(baseDevelopmentFund.changes),
        changes: baseDevelopmentFund.changes,
        requiresInput: baseDevelopmentFund.changes.some(c => c.requiresUserInput),
      },
      // Fritt eget kapital
      {
        label: 'Överkursfond',
        opening: baseSharePremium.opening,
        closing: baseSharePremium.closing !== 0 ? baseSharePremium.closing : baseSharePremium.opening + calculateNetChange(baseSharePremium.changes),
        changes: baseSharePremium.changes,
        requiresInput: baseSharePremium.changes.some(c => c.requiresUserInput),
      },
      {
        label: 'Balanserat resultat',
        opening: baseRetainedEarnings.opening,
        closing: baseRetainedEarnings.closing !== 0 ? baseRetainedEarnings.closing : baseRetainedEarnings.opening + calculateNetChange(baseRetainedEarnings.changes),
        changes: baseRetainedEarnings.changes,
        requiresInput: baseRetainedEarnings.changes.some(c => c.requiresUserInput),
      },
      {
        label: 'Aktieägartillskott',
        opening: baseShareholderContributions.opening,
        closing: baseShareholderContributions.closing !== 0 ? baseShareholderContributions.closing : baseShareholderContributions.opening + calculateNetChange(baseShareholderContributions.changes),
        changes: baseShareholderContributions.changes,
        requiresInput: baseShareholderContributions.changes.some(c => c.requiresUserInput),
      },
    ];

    // Filter out components with no values
    const filteredComponents = components.filter(
      c => c.opening !== 0 || c.closing !== 0 || c.changes.length > 0
    );

    // Calculate totals
    const boundComponents = filteredComponents.filter(c =>
      ['Aktiekapital', 'Reservfond', 'Uppskrivningsfond', 'Fond för utvecklingsutg.'].includes(c.label)
    );
    const freeComponents = filteredComponents.filter(c =>
      ['Överkursfond', 'Balanserat resultat', 'Aktieägartillskott'].includes(c.label)
    );

    const currentYear = equityAnalysis?.currentYearResult ?? currentYearResult;

    const totalOpening = filteredComponents.reduce((sum, c) => sum + c.opening, 0);
    const totalClosing = filteredComponents.reduce((sum, c) => sum + c.closing, 0) + currentYear;
    const totalNetChange = totalClosing - totalOpening;

    return {
      boundComponents,
      freeComponents,
      currentYearResult: currentYear,
      totalOpening,
      totalClosing,
      totalNetChange,
      hasAnyChanges: filteredComponents.some(c => c.changes.length > 0) || currentYear !== 0,
    };
  }, [equityAnalysis, manualEquityChanges, currentYearResult]);

  // Check if there are changes that need user input
  const needsUserInput = useMemo(() => {
    if (!equityAnalysis) return false;
    return equityAnalysis.summary.hasChangesRequiringInput;
  }, [equityAnalysis]);

  const {
    register,
    handleSubmit,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      hasReviewedEquityChanges: userInput.equityChanges?.hasReviewed || false,
      dividendPaidDuringYear: userInput.equityChanges?.dividendPaidDuringYear || 0,
      shareholderContributionType: userInput.equityChanges?.shareholderContributions?.[0]?.type,
      shareholderContributionContributor: userInput.equityChanges?.shareholderContributions?.[0]?.contributorName,
      shareIssueType: userInput.equityChanges?.shareIssue?.type,
      shareIssueDecisionDate: userInput.equityChanges?.shareIssue?.decisionDate,
      proposedDividend: userInput.proposedDividend || 0,
    },
  });

  const proposedDividend = watch("proposedDividend") || 0;
  const hasReviewedEquityChanges = watch("hasReviewedEquityChanges");
  const shareholderContributionType = watch("shareholderContributionType");
  const carriedForward = availableForDistribution - proposedDividend;

  // Check for outstanding shareholder contributions that may need to be repaid first
  const outstandingContributions = equityAnalysis?.shareholderContributions.closing ||
    sieData?.balanceSheet.equityAndLiabilities.equity.details?.shareholderContributions || 0;
  const hasOutstandingContributions = outstandingContributions > 0;
  const isContributionConditional = shareholderContributionType === 'conditional';

  // Validation: when freeEquity is negative, max allowed dividend is 0
  const maxDividend = Math.max(0, freeEquity);
  const isDividendTooHigh = proposedDividend > maxDividend;
  const isDividendNegative = proposedDividend < 0;
  const isFreeEquityNegative = freeEquity < 0;

  // Can proceed only if equity changes reviewed (if there are any needing input)
  const canProceed = !needsUserInput || hasReviewedEquityChanges;

  const onSubmit = async (data: FormData) => {
    if (isDividendTooHigh || isDividendNegative) {
      return;
    }

    if (needsUserInput && !data.hasReviewedEquityChanges) {
      setError("Du måste granska förändringarna i eget kapital innan du kan fortsätta.");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Build equity changes input - MERGE with existing data from Step2!
      // Step2 may have set: dividendPaidDuringYear, groupContributionReceived/Given,
      // shareholderContributions, etc. - we must preserve these.
      const existingEquityChanges = userInput.equityChanges || {};

      const equityChangesInput: Record<string, unknown> = {
        // Preserve all existing equity changes from Step2
        ...existingEquityChanges,
        // Override/add Step4 specific values
        hasReviewed: data.hasReviewedEquityChanges,
        dividendPaidDuringYear: data.dividendPaidDuringYear || existingEquityChanges.dividendPaidDuringYear || 0,
      };

      // Add shareholder contribution details if detected from SIE
      // Only add if SIE detected a change AND user hasn't already specified in Step2
      const shareholderContributionNetChange = equityAnalysis?.shareholderContributions?.netChange ?? 0;
      if (shareholderContributionNetChange !== 0 && shareholderContributionNetChange > 0) {
        // Merge with existing contributions from Step2 if any, or create new
        const existingContributions = existingEquityChanges.shareholderContributions || [];
        const sieDetectedContribution = {
          type: data.shareholderContributionType || 'unconditional' as const,
          amount: shareholderContributionNetChange,
          contributorName: data.shareholderContributionContributor,
        };
        // If Step2 already has contributions, preserve them; otherwise use SIE-detected
        equityChangesInput.shareholderContributions = existingContributions.length > 0
          ? existingContributions
          : [sieDetectedContribution];
      }

      // Add share issue details if detected from SIE
      const shareCapitalNetChange = equityAnalysis?.shareCapital?.netChange ?? 0;
      const sharePremiumNetChange = equityAnalysis?.sharePremium?.netChange ?? 0;
      if (shareCapitalNetChange !== 0 && shareCapitalNetChange > 0) {
        // Merge with existing from Step2 if any
        const existingShareIssue = existingEquityChanges.shareIssue;
        equityChangesInput.shareIssue = {
          ...existingShareIssue,
          type: data.shareIssueType || existingShareIssue?.type || 'cash',
          totalShareCapitalIncrease: shareCapitalNetChange,
          sharePremiumAmount: sharePremiumNetChange > 0 ? sharePremiumNetChange : existingShareIssue?.sharePremiumAmount,
          decisionDate: data.shareIssueDecisionDate || existingShareIssue?.decisionDate,
        };
      }

      const input = {
        proposedDividend: data.proposedDividend,
        equityChanges: equityChangesInput,
        currentStep: 5,
      };

      if (onSaveInput) {
        // Guest mode
        const result = await onSaveInput(input);
        if (result.success) {
          if (result.report) onUpdate(result.report);
          if (onNavigateStep) onNavigateStep(nextStep);
          else navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
        } else {
          setError(result.error || "Kunde inte spara");
        }
      } else {
        // Authenticated mode
        const response = await reportsApi.updateInput(report.id, input);
        if (response.success && response.data) {
          onUpdate(response.data.report);
          if (onNavigateStep) onNavigateStep(nextStep);
          else navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
        } else {
          setError(response.error?.message || "Kunde inte spara");
        }
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Ett fel uppstod vid sparning");
      setError("Ett fel uppstod vid sparning");
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Kapital & Disposition
        </h2>
        <p className="text-gray-600">
          Granska förändringar i eget kapital och ange förslag till vinstdisposition
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* DEL 1: FÖRÄNDRINGAR I EGET KAPITAL */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="card">
        <button
          type="button"
          onClick={() => setShowEquitySection(!showEquitySection)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              needsUserInput && !hasReviewedEquityChanges
                ? "bg-amber-100"
                : hasEquityChanges
                  ? "bg-green-100"
                  : "bg-gray-100"
            }`}>
              <Building2 className={`${
                needsUserInput && !hasReviewedEquityChanges
                  ? "text-amber-600"
                  : hasEquityChanges
                    ? "text-green-600"
                    : "text-gray-600"
              }`} size={20} />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">
                Förändringar i eget kapital
              </h3>
              <p className="text-sm text-gray-500">
                K2 5.7, ÅRL 5 kap 14§
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {hasEquityChanges && (
              <span className={`text-sm px-2 py-1 rounded-full ${
                needsUserInput && !hasReviewedEquityChanges
                  ? "bg-amber-100 text-amber-700"
                  : "bg-green-100 text-green-700"
              }`}>
                {needsUserInput && !hasReviewedEquityChanges
                  ? "Behöver granskas"
                  : "Granskad"}
              </span>
            )}
            {showEquitySection ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </button>

        {showEquitySection && (
          <div className="mt-6 space-y-6">
            {!hasEquityChanges ? (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2 text-gray-600">
                  <Info size={16} />
                  <p className="text-sm">
                    Inga väsentliga förändringar i eget kapital har detekterats under räkenskapsåret.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* ═══════════════════════════════════════════════════════════════════════════ */}
                {/* KONSOLIDERAD EK-TABELL (K2 5.7, ÅRL 5 kap 14§) */}
                {/* Kombinerar SIE-data + manuella ändringar i EN tabell */}
                {/* ═══════════════════════════════════════════════════════════════════════════ */}

                {/* Data source indicator */}
                {(hasManualEquityChanges || equityAnalysis?.summary.hasAnyChanges) && (
                  <div className="flex items-center gap-3 mb-4 text-xs">
                    <span className="text-gray-500">Datakällor:</span>
                    {equityAnalysis?.summary.hasAnyChanges && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md">
                        <CheckCircle2 size={12} />
                        SIE-fil
                      </span>
                    )}
                    {hasManualEquityChanges && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 rounded-md">
                        <CheckCircle2 size={12} />
                        Manuellt (Steg 2)
                      </span>
                    )}
                  </div>
                )}

                {/* Consolidated Equity Changes Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="py-3 text-left font-medium text-gray-700">Komponent</th>
                        <th className="py-3 text-right font-medium text-gray-700">Ingående</th>
                        <th className="py-3 text-right font-medium text-gray-700">Förändringar</th>
                        <th className="py-3 text-right font-medium text-gray-700">Utgående</th>
                        <th className="py-3 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {/* Bundet eget kapital */}
                      {consolidatedEquityData.boundComponents.length > 0 && (
                        <>
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="py-2 font-semibold text-gray-900">Bundet eget kapital</td>
                          </tr>
                          {consolidatedEquityData.boundComponents.map((component) => {
                            const netChange = component.closing - component.opening;
                            if (component.opening === 0 && component.closing === 0 && component.changes.length === 0) return null;

                            return (
                              <tr key={component.label} className={component.requiresInput ? "bg-amber-50" : ""}>
                                <td className="py-3 text-gray-700">{component.label}</td>
                                <td className="py-3 text-right font-mono">{formatNumber(component.opening)}</td>
                                <td className="py-3 text-right">
                                  {component.changes.length > 0 ? (
                                    <div className="space-y-1">
                                      {component.changes.map((change, idx) => (
                                        <div key={idx} className="text-xs">
                                          <span className={change.amount > 0 ? "text-green-600" : "text-red-600"}>
                                            {change.amount > 0 ? "+" : ""}{formatNumber(change.amount)}
                                          </span>
                                          <span className="text-gray-500 ml-1">
                                            ({change.description || formatChangeType(change.type)})
                                          </span>
                                          {change.source === 'manual' && (
                                            <span className="ml-1 text-primary-500 text-[10px]">●</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : netChange !== 0 ? (
                                    <span className={netChange > 0 ? "text-green-600" : "text-red-600"}>
                                      {netChange > 0 ? "+" : ""}{formatNumber(netChange)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="py-3 text-right font-mono font-medium">{formatNumber(component.closing)}</td>
                                <td className="py-3 text-center w-8">
                                  {component.requiresInput ? (
                                    <AlertCircle className="text-amber-500" size={16} />
                                  ) : component.changes.length > 0 ? (
                                    <CheckCircle2 className="text-green-500" size={16} />
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      )}

                      {/* Fritt eget kapital */}
                      {consolidatedEquityData.freeComponents.length > 0 && (
                        <>
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="py-2 font-semibold text-gray-900">Fritt eget kapital</td>
                          </tr>
                          {consolidatedEquityData.freeComponents.map((component) => {
                            const netChange = component.closing - component.opening;
                            if (component.opening === 0 && component.closing === 0 && component.changes.length === 0) return null;

                            return (
                              <tr key={component.label} className={component.requiresInput ? "bg-amber-50" : ""}>
                                <td className="py-3 text-gray-700">{component.label}</td>
                                <td className="py-3 text-right font-mono">{formatNumber(component.opening)}</td>
                                <td className="py-3 text-right">
                                  {component.changes.length > 0 ? (
                                    <div className="space-y-1">
                                      {component.changes.map((change, idx) => (
                                        <div key={idx} className="text-xs">
                                          <span className={change.amount > 0 ? "text-green-600" : "text-red-600"}>
                                            {change.amount > 0 ? "+" : ""}{formatNumber(change.amount)}
                                          </span>
                                          <span className="text-gray-500 ml-1">
                                            ({change.description || formatChangeType(change.type)})
                                          </span>
                                          {change.source === 'manual' && (
                                            <span className="ml-1 text-primary-500 text-[10px]">●</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : netChange !== 0 ? (
                                    <span className={netChange > 0 ? "text-green-600" : "text-red-600"}>
                                      {netChange > 0 ? "+" : ""}{formatNumber(netChange)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="py-3 text-right font-mono font-medium">{formatNumber(component.closing)}</td>
                                <td className="py-3 text-center w-8">
                                  {component.requiresInput ? (
                                    <AlertCircle className="text-amber-500" size={16} />
                                  ) : component.changes.length > 0 ? (
                                    <CheckCircle2 className="text-green-500" size={16} />
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      )}

                      {/* Årets resultat */}
                      <tr className="border-t-2 border-gray-300">
                        <td className="py-3 font-medium text-gray-900">Årets resultat</td>
                        <td className="py-3 text-right font-mono">-</td>
                        <td className="py-3 text-right">
                          <span className={consolidatedEquityData.currentYearResult >= 0 ? "text-green-600" : "text-red-600"}>
                            {consolidatedEquityData.currentYearResult >= 0 ? "+" : ""}{formatNumber(consolidatedEquityData.currentYearResult)}
                          </span>
                        </td>
                        <td className="py-3 text-right font-mono font-medium">
                          {formatNumber(consolidatedEquityData.currentYearResult)}
                        </td>
                        <td className="py-3"></td>
                      </tr>

                      {/* Summa */}
                      <tr className="bg-primary-50 font-semibold">
                        <td className="py-3 text-gray-900">Summa eget kapital</td>
                        <td className="py-3 text-right font-mono">{formatNumber(consolidatedEquityData.totalOpening)}</td>
                        <td className="py-3 text-right">
                          <span className={consolidatedEquityData.totalNetChange >= 0 ? "text-green-700" : "text-red-700"}>
                            {consolidatedEquityData.totalNetChange >= 0 ? "+" : ""}{formatNumber(consolidatedEquityData.totalNetChange)}
                          </span>
                        </td>
                        <td className="py-3 text-right font-mono">{formatNumber(consolidatedEquityData.totalClosing)}</td>
                        <td className="py-3"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Legend for data sources */}
                {hasManualEquityChanges && (
                  <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                    <span className="text-primary-500">●</span>
                    <span>= Manuellt angiven förändring (Steg 2)</span>
                  </div>
                )}

                {/* Details input for changes requiring user input */}
                {needsUserInput && equityAnalysis && (
                  <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-medium text-amber-900 flex items-center space-x-2">
                      <AlertCircle size={18} />
                      <span>Komplettera följande uppgifter:</span>
                    </h4>

                    {/* Shareholder contribution details */}
                    {(equityAnalysis.shareholderContributions?.netChange ?? 0) > 0 && (
                      <div className="bg-white p-4 rounded-lg space-y-3">
                        <p className="font-medium text-gray-900">
                          Aktieägartillskott: {formatNumber(equityAnalysis.shareholderContributions?.netChange ?? 0)} kr
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Typ av tillskott
                            </label>
                            <select
                              {...register("shareholderContributionType")}
                              className="input"
                            >
                              <option value="unconditional">Ovillkorat</option>
                              <option value="conditional">Villkorat</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Tillskottsgivare (valfritt)
                            </label>
                            <input
                              type="text"
                              {...register("shareholderContributionContributor")}
                              className="input"
                              placeholder="T.ex. Ägare AB"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Share issue details */}
                    {(equityAnalysis.shareCapital?.netChange ?? 0) > 0 && (
                      <div className="bg-white p-4 rounded-lg space-y-3">
                        <p className="font-medium text-gray-900">
                          Ökning av aktiekapital: {formatNumber(equityAnalysis.shareCapital?.netChange ?? 0)} kr
                          {(equityAnalysis.sharePremium?.netChange ?? 0) > 0 && (
                            <span className="text-gray-600">
                              {" "}(+ {formatNumber(equityAnalysis.sharePremium?.netChange ?? 0)} kr i överkurs)
                            </span>
                          )}
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Typ av emission
                            </label>
                            <select
                              {...register("shareIssueType")}
                              className="input"
                            >
                              <option value="cash">Nyemission (kontant)</option>
                              <option value="non_cash">Apportemission</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Beslutsdatum (valfritt)
                            </label>
                            <input
                              type="date"
                              {...register("shareIssueDecisionDate")}
                              className="input"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Dividend paid during year */}
                    {equityAnalysis.retainedEarnings?.changes?.some(c => c.type === 'dividend_paid') && (
                      <div className="bg-white p-4 rounded-lg space-y-3">
                        <p className="font-medium text-gray-900">
                          Utbetald utdelning (beslutad föregående årsstämma)
                        </p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bekräfta belopp
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              {...register("dividendPaidDuringYear", { valueAsNumber: true })}
                              className="input pr-12"
                              defaultValue={Math.abs(equityAnalysis.retainedEarnings?.netChange ?? 0)}
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">kr</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Confirmation checkbox */}
                    <div className="flex items-start space-x-3 pt-2">
                      <input
                        type="checkbox"
                        id="hasReviewedEquityChanges"
                        {...register("hasReviewedEquityChanges")}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <label htmlFor="hasReviewedEquityChanges" className="text-sm text-gray-700">
                        Jag har granskat och kompletterat uppgifterna om förändringar i eget kapital.
                        <span className="text-red-500">*</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Info box when no input needed */}
                {!needsUserInput && hasEquityChanges && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-2 text-green-800">
                      <CheckCircle2 size={18} />
                      <p className="text-sm">
                        {hasManualEquityChanges && equityAnalysis?.summary.hasAnyChanges
                          ? "Förändringar i eget kapital har detekterats från bokföringsdata och kompletterats med manuella uppgifter."
                          : hasManualEquityChanges
                            ? "Förändringar i eget kapital har angetts manuellt i Steg 2."
                            : isManual
                              ? "Förändringar i eget kapital baseras på angivna bokföringsvärden."
                              : "Alla förändringar i eget kapital har detekterats automatiskt från SIE-filen."}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* DEL 2: RESULTATDISPOSITION */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}

      {/* Available amounts */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <Wallet className="text-primary-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Till bolagsstämmans förfogande står
            </h3>
            <p className="text-sm text-gray-500">K2 5.8 - Resultatdisposition</p>
          </div>
        </div>

        <table className="w-full">
          <tbody className="divide-y divide-gray-100">
            <tr>
              <td className="py-3 text-gray-600">Balanserat resultat</td>
              <td className="py-3 text-right font-medium">
                {formatNumber(retainedEarnings)} kr
              </td>
            </tr>
            <tr>
              <td className="py-3 text-gray-600">Årets resultat</td>
              <td className={`py-3 text-right font-medium ${
                currentYearResult >= 0 ? "" : "text-red-600"
              }`}>
                {formatNumber(currentYearResult)} kr
              </td>
            </tr>
            <tr className="bg-gray-50">
              <td className="py-3 font-semibold text-gray-900">Summa</td>
              <td className={`py-3 text-right font-bold ${
                availableForDistribution >= 0 ? "text-gray-900" : "text-red-600"
              }`}>
                {formatNumber(availableForDistribution)} kr
              </td>
            </tr>
          </tbody>
        </table>

        {/* Free equity info */}
        <div className={`mt-4 p-3 rounded-lg ${isFreeEquityNegative ? "bg-amber-50" : "bg-blue-50"}`}>
          <div className="flex items-start space-x-2">
            {isFreeEquityNegative ? (
              <AlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={16} />
            ) : (
              <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
            )}
            <p className={`text-sm ${isFreeEquityNegative ? "text-amber-800" : "text-blue-800"}`}>
              <strong>Fritt eget kapital:</strong> {formatNumber(freeEquity)} kr
              <br />
              <span className={isFreeEquityNegative ? "text-amber-600" : "text-blue-600"}>
                {isFreeEquityNegative
                  ? "Fritt eget kapital är negativt. Ingen utdelning kan föreslås."
                  : "Utdelning får inte överstiga fritt eget kapital."}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Proposal */}
      <div className="card">
        <h3 className="font-semibold text-gray-900 mb-6">
          Styrelsen föreslår
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Till aktieägarna utdelas
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                step="1"
                {...register("proposedDividend", {
                  valueAsNumber: true,
                  min: { value: 0, message: "Utdelningen kan inte vara negativ" },
                })}
                className={`input pr-12 ${
                  isDividendTooHigh || isDividendNegative
                    ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                    : ""
                }`}
                placeholder="0"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                kr
              </span>
            </div>

            {isDividendTooHigh && (
              <div className="mt-2 flex items-center space-x-2 text-red-600">
                <AlertCircle size={16} />
                <p className="text-sm">
                  {isFreeEquityNegative
                    ? "Ingen utdelning kan ske när fritt eget kapital är negativt."
                    : `Utdelningen överstiger fritt eget kapital (${formatNumber(freeEquity)} kr)`}
                </p>
              </div>
            )}

            {/* Warning: Conditional shareholder contributions should be repaid first */}
            {proposedDividend > 0 && hasOutstandingContributions && (
              <div className={`mt-3 p-3 rounded-lg ${
                isContributionConditional ? "bg-amber-50 border border-amber-200" : "bg-blue-50 border border-blue-200"
              }`}>
                <div className="flex items-start space-x-2">
                  {isContributionConditional ? (
                    <AlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={16} />
                  ) : (
                    <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                  )}
                  <div className={`text-sm ${isContributionConditional ? "text-amber-800" : "text-blue-800"}`}>
                    <strong>Aktieägartillskott: {formatNumber(outstandingContributions)} kr</strong>
                    <p className="mt-1">
                      {isContributionConditional ? (
                        <>
                          <strong>OBS!</strong> Det finns villkorade aktieägartillskott som enligt praxis
                          ska återbetalas <em>innan</em> utdelning sker till övriga aktieägare.
                          Säkerställ att detta beaktas i förslaget till vinstdisposition.
                        </>
                      ) : (
                        <>
                          Det finns utestående aktieägartillskott i bolaget.
                          Om dessa är <em>villkorade</em> ska de normalt återbetalas innan utdelning sker.
                          Ange typen av tillskott i avsnittet "Förändringar i eget kapital" ovan.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">I ny räkning överföres</span>
              <span className={`font-bold text-lg ${
                carriedForward >= 0 ? "text-gray-900" : "text-red-600"
              }`}>
                {formatNumber(carriedForward)} kr
              </span>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900">Summa</span>
              <span className="font-bold text-lg text-gray-900">
                {formatNumber(availableForDistribution)} kr
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="card bg-gray-50">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Förhandsvisning i årsredovisningen:
        </h4>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm">
          <p className="font-medium mb-2">Förslag till vinstdisposition</p>
          <p className="mb-2">Till bolagsstämmans förfogande står:</p>
          <table className="w-full mb-2">
            <tbody>
              <tr>
                <td className="py-1">Balanserat resultat</td>
                <td className="py-1 text-right">{formatNumber(retainedEarnings)} kr</td>
              </tr>
              {(sieData?.balanceSheet.equityAndLiabilities.equity.details?.shareholderContributions ?? 0) !== 0 && (
                <tr>
                  <td className="py-1 pl-4 text-xs text-gray-500">varav aktieägartillskott</td>
                  <td className="py-1 text-right text-xs text-gray-500">
                    {formatNumber(sieData?.balanceSheet.equityAndLiabilities.equity.details?.shareholderContributions ?? 0)} kr
                  </td>
                </tr>
              )}
              <tr>
                <td className="py-1">Årets resultat</td>
                <td className="py-1 text-right">{formatNumber(currentYearResult)} kr</td>
              </tr>
              <tr className="border-t">
                <td className="py-1 font-medium">Summa</td>
                <td className="py-1 text-right font-medium">{formatNumber(availableForDistribution)} kr</td>
              </tr>
            </tbody>
          </table>
          <p className="mb-2">Styrelsen föreslår:</p>
          <table className="w-full">
            <tbody>
              <tr>
                <td className="py-1">Till aktieägarna utdelas</td>
                <td className="py-1 text-right">{formatNumber(proposedDividend)} kr</td>
              </tr>
              <tr>
                <td className="py-1">I ny räkning överföres</td>
                <td className="py-1 text-right">{formatNumber(carriedForward)} kr</td>
              </tr>
              <tr className="border-t">
                <td className="py-1 font-medium">Summa</td>
                <td className="py-1 text-right font-medium">{formatNumber(availableForDistribution)} kr</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={() => {
            window.scrollTo(0, 0);
            onNavigateStep ? onNavigateStep(prevStep) : navigate(`/dashboard/wizard/${report.id}/step/${prevStep}`);
          }}
          className="btn btn-secondary flex items-center"
        >
          <ArrowLeft size={20} className="mr-2" />
          Tillbaka
        </button>

        <button
          type="submit"
          disabled={isSaving || isDividendTooHigh || isDividendNegative || !canProceed}
          className="btn btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            "Sparar..."
          ) : (
            <>
              Fortsätt
              <ArrowRight size={20} className="ml-2" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
